import { TICKS_PER_LOOP } from '@arpeggi/shared'
import { useEffect, useRef } from 'react'
import { INoteCollectionStore } from '../../../stores/song/loop/NoteCollectionStore'

const DPI_SCALE = devicePixelRatio * 2 || 2
const MIN_HEIGHT = DPI_SCALE * 2
const MAX_HEIGHT = DPI_SCALE * 7
const MELODIC_MAX_HEIGHT = DPI_SCALE * 3
const MARGIN = DPI_SCALE / 2

const findMinMaxRowRange = (notes: INoteCollectionStore): [min: number, max: number] => {
  if (notes.notes.length === 0) return [0, -1]
  let n: number = Infinity,
    m: number = -Infinity
  notes.notes.forEach((note) => {
    n = Math.min(n, note.note)
    m = Math.max(m, note.note)
  })
  return [n, m]
}

const drawNotes = (
  context: CanvasRenderingContext2D,
  notes: INoteCollectionStore,
  width: number,
  height: number,
  isDrums?: boolean
) => {
  const [min, max] = findMinMaxRowRange(notes)
  const diff = max - min
  const segmentHeight = height / (diff + 1)
  const realSegmentHeight = Math.min(
    Math.max(MIN_HEIGHT, segmentHeight),
    isDrums ? MAX_HEIGHT : MELODIC_MAX_HEIGHT
  )
  const spacingY = Math.min(segmentHeight, realSegmentHeight)
  notes.notes.forEach((note) => {
    const segmentWidth = note.duration / TICKS_PER_LOOP
    const reversedIndex = isDrums ? note.note - min : max - note.note
    context.fillRect(
      (note.start * width) / TICKS_PER_LOOP + MARGIN,
      reversedIndex * spacingY + MARGIN,
      segmentWidth * width - MARGIN,
      realSegmentHeight - MARGIN
    )
  })
}

const redrawCanvas = (
  canvas: HTMLCanvasElement,
  notes: INoteCollectionStore,
  fill: string,
  isDrums?: boolean
) => {
  const { width, height } = canvas
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = fill
  if (notes.notes.length === 0) return
  drawNotes(ctx, notes, width, height, isDrums)
}

const useCanvasVisualizer = (
  notes: INoteCollectionStore,
  loading: boolean,
  instrumentLoopWidthPX?: number,
  fill: string = '#ffffff',
  isDrums?: boolean
) => {
  const canvasRef = useRef<HTMLCanvasElement>()

  useEffect(() => {
    if (loading) return
    const canvas = canvasRef.current
    const sizeObserver = new ResizeObserver((entries) => {
      if (!canvas) return
      if (!entries[0]) return
      const { width, height } = entries[0].contentRect
      canvas.width = Math.floor(DPI_SCALE * width)
      canvas.height = Math.floor(DPI_SCALE * height)
      requestAnimationFrame(() => redrawCanvas(canvas, notes, fill, isDrums))
    })
    sizeObserver.observe(canvas)
    return () => sizeObserver.disconnect()
  }, [JSON.stringify(notes), loading, fill])

  return canvasRef
}

export default useCanvasVisualizer
